import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  TreeStructure,
  ShieldCheck,
  MapTrifold,
  Archive,
  Users,
  Church,
  Globe,
  GraduationCap,
  ArrowRight,
} from "@phosphor-icons/react";

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "Private Family Circle",
    body:
      "An invite-only space where only verified relatives belong. No ads, no strangers, no data harvesting — just the people who matter.",
  },
  {
    icon: MapTrifold,
    title: "Interactive Family Map",
    body:
      "Watch the tree assemble itself. Every parent, sibling and spouse link draws the next branch — confirmed by the family, visualized in seconds.",
  },
  {
    icon: Archive,
    title: "Preserve Family History",
    body:
      "Memorials, photo archives, oral histories and stories — kept in one place that outlives any social feed, ready for the next generation.",
  },
];

const AUDIENCES = [
  {
    icon: Users,
    title: "Families",
    body: "Map every relative across cousins, in-laws and generations — and stay in touch between reunions.",
  },
  {
    icon: Church,
    title: "Church Families",
    body: "Track the spiritual family — godparents, mentors, ministry circles — and welcome new members with context.",
  },
  {
    icon: Globe,
    title: "Cultural Groups",
    body: "Honor lineages, clans and heritage networks. Preserve names and stories that don't fit a generic social network.",
  },
  {
    icon: GraduationCap,
    title: "Class Reunions & Organizations",
    body: "Keep a verified directory of who's who, who married whom, and who you owe a phone call.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0E1E18] text-ivory antialiased selection:bg-gold/30 selection:text-ivory">
      {/* Decorative ambient glow */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-gold/10 blur-3xl" />
        <div className="absolute top-1/2 -right-40 w-[520px] h-[520px] rounded-full bg-forest/40 blur-3xl" />
      </div>

      {/* Top bar */}
      <header className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex items-center justify-between" data-testid="landing-header">
        <div className="flex items-center gap-2.5">
          <span className="w-10 h-10 rounded-full bg-gold/15 text-gold flex items-center justify-center border border-gold/30">
            <TreeStructure size={22} weight="duotone" />
          </span>
          <span className="font-display text-2xl tracking-tight">LegacyLink</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/login">
            <Button
              variant="ghost"
              className="text-ivory/80 hover:text-ivory hover:bg-white/5 rounded-full"
              data-testid="header-login-button"
            >
              Sign in
            </Button>
          </Link>
          <Link to="/register">
            <Button
              className="bg-gold text-[#0E1E18] hover:bg-gold/90 rounded-full px-4 sm:px-5 font-semibold"
              data-testid="header-register-button"
            >
              Create account
            </Button>
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pt-10 md:pt-20 pb-24" data-testid="hero-section">
        <div className="max-w-4xl">
          <span className="inline-flex items-center gap-2 text-[11px] sm:text-xs tracking-[0.28em] uppercase text-gold mb-6 sm:mb-8">
            <span className="w-8 h-px bg-gold/60" />
            For the family that wants to remember
          </span>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight"
            data-testid="hero-headline"
          >
            Know Where You Come From.
            <br />
            <span className="text-gold italic">Stay Connected.</span>
            <br />
            Preserve What Matters.
          </h1>
          <p
            className="mt-7 sm:mt-9 text-base sm:text-lg lg:text-xl text-ivory/75 max-w-2xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            LegacyLink is the private space where your family maps how everyone is connected,
            stays in touch, and keeps history alive for the next generation.
          </p>
          <div className="mt-9 sm:mt-11 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Link to="/register" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto h-12 px-7 bg-gold text-[#0E1E18] hover:bg-gold/90 rounded-full font-semibold tracking-wide"
                data-testid="hero-create-circle-button"
              >
                Create Your Circle <ArrowRight size={18} weight="bold" className="ml-1.5" />
              </Button>
            </Link>
            <Link to="/login" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto h-12 px-7 border-ivory/30 text-ivory hover:bg-white/5 hover:text-ivory rounded-full bg-transparent"
                data-testid="hero-join-circle-button"
              >
                Join a Circle
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-t border-white/5" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 lg:py-28">
          <div className="max-w-2xl mb-12 lg:mb-16">
            <p className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-gold mb-4">What makes it different</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              A quiet corner of the internet, built only for your people.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8" data-testid="features-grid">
            {FEATURES.map((f, i) => (
              <article
                key={i}
                className="group relative rounded-2xl p-7 lg:p-8 bg-white/[0.03] border border-white/8 hover:border-gold/40 hover:bg-white/[0.05] transition-all duration-300"
                data-testid={`feature-card-${i}`}
              >
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gold/10 text-gold border border-gold/25 mb-6 group-hover:bg-gold/20 transition-colors">
                  <f.icon size={26} weight="duotone" />
                </span>
                <h3 className="font-display text-2xl mb-3 leading-snug">{f.title}</h3>
                <p className="text-ivory/65 leading-relaxed text-[15px]">{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-t border-white/5 bg-[#0A1612]" data-testid="audiences-section">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 lg:py-28">
          <div className="max-w-2xl mb-12 lg:mb-16">
            <p className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-gold mb-4">Who it's for</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Wherever family is found — by blood, faith, heritage, or history.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6" data-testid="audiences-grid">
            {AUDIENCES.map((a, i) => (
              <article
                key={i}
                className="rounded-2xl p-6 lg:p-7 bg-white/[0.025] border border-white/8 hover:border-gold/40 transition-colors"
                data-testid={`audience-card-${i}`}
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-lg bg-gold/10 text-gold border border-gold/25 mb-5">
                  <a.icon size={22} weight="duotone" />
                </span>
                <h3 className="font-display text-xl mb-2 leading-tight">{a.title}</h3>
                <p className="text-ivory/65 text-sm leading-relaxed">{a.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="border-t border-white/5">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20 lg:py-24 text-center">
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
            Start the family record that <span className="text-gold italic">outlives you</span>.
          </h2>
          <p className="mt-5 text-ivory/70 max-w-xl mx-auto leading-relaxed">
            Create your circle in under a minute. Invite the first relative. Watch the map grow as the family confirms how everyone connects.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link to="/register" className="w-full sm:w-auto">
              <Button
                size="lg"
                className="w-full sm:w-auto h-12 px-7 bg-gold text-[#0E1E18] hover:bg-gold/90 rounded-full font-semibold tracking-wide"
                data-testid="footer-create-circle-button"
              >
                Create Your Circle
              </Button>
            </Link>
            <Link to="/login" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto h-12 px-7 border-ivory/30 text-ivory hover:bg-white/5 hover:text-ivory rounded-full bg-transparent"
                data-testid="footer-join-circle-button"
              >
                Join a Circle
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/5 bg-[#0A1612]" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-20 lg:py-24">
          <div className="mb-10 lg:mb-14">
            <p className="text-[11px] sm:text-xs tracking-[0.28em] uppercase text-gold mb-4">Common questions</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Everything you might ask before joining.
            </h2>
          </div>
          <div className="divide-y divide-white/8" data-testid="faq-list">
            {[
              {
                q: "How do I join a circle?",
                a: "An existing circle member sends you an invite link or code. Create your account, paste the code (or follow the link), and the circle's admin will see your join request. Once approved, you'll complete a quick onboarding form so the family map can place you in the tree.",
              },
              {
                q: "How does the family map work?",
                a: "Every member declares their direct relatives — parents, siblings, spouse, children, and family branch. Other members confirm those claims. From that scaffolding, LegacyLink calculates every cousin, aunt, niece and grandparent automatically and draws them on an interactive map.",
              },
              {
                q: "How do I add a memorial?",
                a: "Open the Memorials tab inside your circle and click 'Add memorial.' Enter the person's name, birth and death years, branch and a short bio. Once five family members confirm the entry, the memorial appears on the family map with a silver ring honoring them.",
              },
              {
                q: "Is my information private?",
                a: "Yes. LegacyLink is invite-only — strangers can't find or browse your circle. We never sell data, never run ads, and don't share your tree with anyone outside the family. By default your email is hidden from other members; you can opt-in to show it in your profile settings.",
              },
              {
                q: "Who can see my profile?",
                a: "Only verified members of the same circle. Profile photo, name and bio are visible to everyone in the circle. Your email stays hidden unless you choose to share it. Admins can always see emails to keep the circle moderated.",
              },
            ].map((item, i) => (
              <details key={i} className="group py-5 lg:py-6" data-testid={`faq-item-${i}`}>
                <summary className="flex items-center justify-between gap-6 cursor-pointer list-none">
                  <h3 className="font-display text-lg sm:text-xl lg:text-2xl text-ivory pr-4">{item.q}</h3>
                  <span className="shrink-0 w-9 h-9 rounded-full border border-gold/40 text-gold flex items-center justify-center transition-transform group-open:rotate-45">
                    <span className="text-xl leading-none">+</span>
                  </span>
                </summary>
                <p className="mt-4 text-ivory/65 leading-relaxed max-w-3xl">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-sm text-ivory/40">
        <p>© {new Date().getFullYear()} LegacyLink · Built for families, not feeds.</p>
      </footer>
    </div>
  );
}
